import { Component, inject, computed } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../auth/services/auth.service';
import { UserRole, KEYCLOAK_GROUPS } from '../../models/user.model';
import { UserDisplayComponent } from '../../shared/components/user-display/user-display.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SessionManagerService } from '../../auth/services/session-manager.service';

interface NavItem {
  label: string;
  icon: string;
  route: string;
  requiredPermission?: keyof import('../../models/user.model').UserPermissions;
  requiredRoles?: (UserRole | string)[];
  requiredGroups?: string[];
  requireAll?: boolean; // If true, user must have ALL requirements; if false, ANY is sufficient
}

@Component({
  selector: 'app-side-nav',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, UserDisplayComponent],
  template: `
    <div class="side-nav">
      <div class="logo-container">
        <img
          src="assets/images/ics_logo.webp"
          alt="Integrated Care Solutions"
        />
      </div>
      <ul class="nav-menu">
        @for (item of mainNavItems(); track item.label) {
          <li
            class="nav-item"
            [ngClass]="{ active: isActive(item.route) }"
            [routerLink]="item.route"
            routerLinkActive="active"
            [routerLinkActiveOptions]="{ exact: true }"
            [attr.data-tutorial]="getTutorialDataAttr(item.label)"
          >
            @if (getNavIcon(item.label, isActive(item.route))) {
              <div
                class="icon-svg"
                [innerHTML]="getNavIcon(item.label, isActive(item.route))"
              ></div>
            } @else {
              <i class="pi {{ item.icon }}"></i>
            }
            <span>{{ item.label }}</span>
          </li>
        }
      </ul>

      @if (authService.currentUser(); as user) {
        <div class="user-info">
          <div class="user-details">
            <app-user-display
              [userId]="user.id"
              size="lg"
              variant="detailed"
              [showEmail]="true"
              [showRole]="true"
              [useHospitalFallbackIcon]="false"
              class="current-user-display"
            ></app-user-display>
          </div>
          <button class="logout-btn" (click)="logout()" title="Logout">
            <i class="pi pi-sign-out"></i>
          </button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .side-nav {
        width: 280px;
        background: var(--background-card);
        color: var(--text-color);
        display: flex;
        flex-direction: column;
        height: 100%;
        font-family: var(--font-family-base);
        box-shadow: 2px 0 8px rgba(0, 0, 0, 0.08);
        overflow: hidden;
        position: relative;
        box-sizing: border-box;
      }

      .logo-container {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 32px 20px 24px 20px;
        width: 100%;
        border-bottom: 1px solid var(--border-color);
        height: 96px;
        box-sizing: border-box;
        flex-shrink: 0;
      }

      .logo-container img {
        height: 40px;
        width: auto;
        display: block;
      }

      .nav-menu {
        list-style: none;
        padding: 20px 20px;
        margin: 0;
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
      }

      .nav-item {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        padding: 0 20px;
        border-radius: 8px;
        font-size: 18px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        background: transparent;
        color: var(--text-muted);
        border: 1px solid transparent;
        text-decoration: none;
        position: relative;
        height: 56px;
        box-sizing: border-box;
        flex-shrink: 0;
        width: 100%;
        left: 0;
        right: 0;
        overflow: hidden;
      }

      .nav-item:hover {
        background: var(--hover-background-color);
        color: var(--accent-color);
        border-color: var(--border-color-light);
        transform: translateX(2px);
      }

      .nav-item.active {
        background: rgba(18, 128, 158, 0.1);
        color: var(--accent-color);
        border-color: transparent;
        border-left: 3px solid var(--accent-color);
        font-weight: 600;
      }

      .nav-item .icon-svg {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        margin: 0;
        position: absolute;
        left: 20px;
        top: 50%;
        transform: translateY(-50%);
      }

      .nav-item i {
        font-size: 20px;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        margin: 0;
        text-align: center;
        position: absolute;
        left: 20px;
        top: 50%;
        transform: translateY(-50%);
      }

      .nav-item span {
        white-space: nowrap;
        text-overflow: ellipsis;
        line-height: 1.3;
        margin: 0;
        text-align: left;
        position: absolute;
        left: 56px;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
      }

      .user-info {
        margin-top: auto;
        padding: 0 20px;
        background: var(--background-color);
        border-top: 1px solid var(--border-color-light);
        display: flex;
        align-items: center;
        justify-content: space-between;
        position: relative;
        height: 80px;
        box-sizing: border-box;
        flex-shrink: 0;
        width: 100%;
      }

      .user-info::before {
        content: '';
        position: absolute;
        top: 0;
        left: 20px;
        right: 20px;
        height: 1px;
        background: linear-gradient(
          90deg,
          transparent,
          var(--border-color-input),
          transparent
        );
      }

      .user-details {
        display: flex;
        align-items: center;
        min-width: 0;
        height: 100%;
        position: absolute;
        left: 20px;
        right: 60px;
        top: 0;
      }

      .user-details > i {
        margin: 0;
        font-size: 1.3rem;
        color: var(--text-heading);
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
      }

      .user-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
        justify-content: center;
        margin: 0;
        position: absolute;
        left: 40px;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
      }

      .user-name {
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0;
        color: var(--text-heading);
        line-height: 1.3;
      }

      .user-email {
        font-size: 0.9rem;
        color: var(--text-muted);
        line-height: 1.2;
        margin: 0;
      }

      .user-roles,
      .user-groups {
        font-size: 0.8rem;
        color: var(--text-muted);
        margin: 0;
        line-height: 1.2;
      }

      .logout-btn {
        background: var(--background-card);
        border: 1px solid var(--border-color);
        color: var(--text-muted);
        cursor: pointer;
        padding: 10px;
        border-radius: 6px;
        transition: all 0.2s ease;
        margin: 0;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        box-sizing: border-box;
        position: absolute;
        right: 20px;
        top: 50%;
        transform: translateY(-50%);
      }

      .logout-btn:hover {
        background: var(--hover-background-color);
        color: var(--accent-color);
        border-color: var(--border-color-input);
        transform: translateY(-50%) scale(1.05);
      }

      .session-timer {
        display: flex;
        flex-direction: column;
        font-size: 0.75rem;
        color: var(--text-muted);
        line-height: 1.2;
      }

      .timer-label {
        text-transform: uppercase;
        font-weight: 600;
        opacity: 0.8;
        letter-spacing: 0.5px;
      }

      .timer-value {
        font-family:
          'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        letter-spacing: 0.05em;
        font-weight: 500;
      }

      /* Mobile responsiveness */
      @media (max-width: 768px) {
        .side-nav {
          width: 200px;
        }

        .nav-menu {
          padding: 16px 12px;
        }

        .nav-item {
          padding: 14px 16px;
          font-size: 17px;
          gap: 14px;
          height: 52px;
        }

        .logo-container {
          padding: 24px 16px 16px 16px;
        }

        .logo-container img {
          height: 32px;
        }

        .user-info {
          padding: 16px 12px;
        }
      }

      @media (max-width: 480px) {
        .side-nav {
          width: 180px;
        }

        .nav-menu {
          padding: 12px 8px;
        }

        .nav-item {
          padding: 12px 12px;
          font-size: 16px;
          justify-content: center;
          height: 48px;
        }

        .nav-item span {
          display: none;
        }

        .nav-item .icon-svg,
        .nav-item i {
          margin: 0;
          width: 24px;
          height: 24px;
        }

        .logo-container {
          padding: 20px 12px 12px 12px;
        }

        .logo-container img {
          height: 28px;
        }

        .user-info {
          padding: 12px 8px;
          flex-direction: column;
          gap: 8px;
        }

        .logout-btn {
          margin-left: 0;
          width: 100%;
        }
      }
    `,
  ],
})
export class SideNavComponent {
  protected authService = inject(AuthService);
  public sessionManager = inject(SessionManagerService);
  private router = inject(Router);
  constructor(private sanitizer: DomSanitizer) {}

  dashboardSvg: SafeHtml | undefined = undefined;
  programsSvg: SafeHtml | undefined = undefined;
  fallbackDashboardIcon = 'pi-th-large';
  fallbackProgramsIcon = 'pi-list';

  ngOnInit() {
    // No need to assign dashboardSvg/programsSvg here; handled by getDashboardSvg/getProgramsSvg
  }

  /**
   * Returns an inline SVG string for the given nav item label, using the
   * icon assets from assets/icons/. Returns null for items that have no
   * matching asset icon so the template falls back to the PrimeIcons class.
   */
  getNavIcon(label: string, active: boolean): string | null {
    const fill = active ? 'var(--accent-color)' : 'var(--text-muted)';
    switch (label) {
      case 'Referrals':
        // Inquiry.svg
        return this.sanitizer.bypassSecurityTrustHtml(
          `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 2C19.1046 2 20 2.89543 20 4V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4C4 2.89543 4.89543 2 6 2H18ZM8 16.5C7.44772 16.5 7 16.9477 7 17.5C7 18.0523 7.44772 18.5 8 18.5C8.55228 18.5 9 18.0523 9 17.5C9 16.9477 8.55228 16.5 8 16.5ZM12 16.5C11.4477 16.5 11 16.9477 11 17.5C11 18.0523 11.4477 18.5 12 18.5H16C16.5523 18.5 17 18.0523 17 17.5C17 16.9477 16.5523 16.5 16 16.5H12ZM8 13C7.44772 13 7 13.4477 7 14C7 14.5523 7.44772 15 8 15C8.55228 15 9 14.5523 9 14C9 13.4477 8.55228 13 8 13ZM12 13C11.4477 13 11 13.4477 11 14C11 14.5523 11.4477 15 12 15H16C16.5523 15 17 14.5523 17 14C17 13.4477 16.5523 13 16 13H12ZM8 9.5C7.44772 9.5 7 9.94771 7 10.5C7 11.0523 7.44772 11.5 8 11.5C8.55228 11.5 9 11.0523 9 10.5C9 9.94771 8.55228 9.5 8 9.5ZM12 9.5C11.4477 9.5 11 9.94771 11 10.5C11 11.0523 11.4477 11.5 12 11.5H16C16.5523 11.5 17 11.0523 17 10.5C17 9.94771 16.5523 9.5 16 9.5H12ZM8 6C7.44772 6 7 6.44772 7 7C7 7.55228 7.44772 8 8 8C8.55228 8 9 7.55228 9 7C9 6.44772 8.55228 6 8 6ZM12 6C11.4477 6 11 6.44772 11 7C11 7.55228 11.4477 8 12 8H16C16.5523 8 17 7.55228 17 7C17 6.44772 16.5523 6 16 6H12Z" fill="${fill}"/></svg>`,
        ) as string;
      case 'Hospitals':
        // Clinic.svg
        return this.sanitizer.bypassSecurityTrustHtml(
          `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 19.5L20.1025 19.5049C20.6067 19.5562 21 19.9823 21 20.5C21 21.0177 20.6067 21.4438 20.1025 21.4951L20 21.5H4C3.44772 21.5 3 21.0523 3 20.5C3 19.9477 3.44772 19.5 4 19.5V12H20V19.5ZM10 13.5C8.89543 13.5 8 14.3954 8 15.5V19.5H11.25V13.5H10ZM12.75 19.5H16V15.5C16 14.3954 15.1046 13.5 14 13.5H12.75V19.5ZM7.5 7.5C7.5 8.60457 8.39543 9.5 9.5 9.5H14.5C15.6046 9.5 16.5 8.60457 16.5 7.5V4.5H17.7637C18.5212 4.5 19.2139 4.92796 19.5527 5.60547L20.8291 8.1582C20.9414 8.38284 21 8.63069 21 8.88184C21 9.77545 20.2755 10.5 19.3818 10.5H4.61816C3.72455 10.5 3 9.77545 3 8.88184C3.00002 8.63069 3.05858 8.38284 3.1709 8.1582L4.44727 5.60547C4.78607 4.92796 5.47882 4.5 6.23633 4.5H7.5V7.5ZM14 2C14.5523 2 15 2.44772 15 3V7C15 7.55228 14.5523 8 14 8H10C9.44772 8 9 7.55228 9 7V3C9 2.44772 9.44772 2 10 2H14ZM12 3C11.6318 3 11.333 3.2988 11.333 3.66699V4.33301H10.667C10.2988 4.33301 10 4.63181 10 5C10 5.36819 10.2988 5.66699 10.667 5.66699H11.333V6.33301C11.333 6.7012 11.6318 7 12 7C12.3682 7 12.667 6.7012 12.667 6.33301V5.66699H13.333C13.7012 5.66699 14 5.36819 14 5C14 4.63181 13.7012 4.33301 13.333 4.33301H12.667V3.66699C12.667 3.2988 12.3682 3 12 3Z" fill="${fill}"/></svg>`,
        ) as string;
      case 'Hospital Users':
        // Nursing.svg
        return this.sanitizer.bypassSecurityTrustHtml(
          `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.18697 1H15.8809C17.7066 1 19.109 2.61697 18.8508 4.42426L18.5599 6.46053C19.3252 7.20865 20.0176 8.1534 20.5175 9.29709C22.3977 13.5986 21.0339 20.0084 19.8822 20.2884C19.4653 20.3897 18.4898 20.2494 17.3257 19.8249C17.1017 20.0837 16.8662 20.3226 16.6258 20.5401C15.3828 21.6648 13.6756 22.5215 12.0339 22.5215C10.3923 22.5215 8.6851 21.6648 7.44206 20.5401C7.22411 20.3429 7.01012 20.1281 6.80513 19.8968C5.52845 20.4071 4.45881 20.5422 4.07653 20.2886C3.08885 19.6334 1.56109 13.5988 3.44125 9.2973C4.03197 7.94585 4.71156 6.87085 5.45218 6.06973L5.21712 4.42426C4.95893 2.61696 6.36132 1 8.18697 1ZM7.91466 9.16325L7.9116 9.14351C7.9115 9.14282 7.9114 9.14212 7.9113 9.14142L7.19702 4.14142C7.11095 3.53899 7.57842 3 8.18697 3H15.8809C16.4895 3 16.957 3.53899 16.8709 4.14142L16.7352 5.09109C16.7352 5.09108 16.7352 5.09107 16.7352 5.09106L16.1566 9.14141C16.0862 9.63406 15.6643 9.99999 15.1666 9.99999H11.337L11.337 9.9999H8.90121C8.48962 9.9999 8.12983 9.74959 7.97788 9.3843C7.94886 9.3145 7.92742 9.2405 7.91466 9.16325ZM10.0273 12C9.81016 12.2042 9.57093 12.3982 9.31136 12.5778C8.73093 12.9793 8.11854 13.26 7.52676 13.4185C7.21437 14.0107 7.03394 14.7117 7.03394 15.5215C7.03394 16.8471 7.72526 18.0522 8.66542 18.9471C9.68375 19.9163 10.994 20.5215 12.0339 20.5215C13.0793 20.5215 14.3978 19.91 15.4184 18.9318L15.4184 18.9318C16.3504 18.0385 17.0339 16.8395 17.0339 15.5214C17.0339 15.4586 17.0328 15.3964 17.0307 15.3349C17.0234 15.1272 17.004 14.9269 16.9734 14.7341C16.8542 13.9844 16.5649 13.3476 16.1567 12.8237C15.365 13.1186 14.3652 13.1973 13.3238 12.9915C12.4331 12.8155 11.6497 12.4586 11.0537 12H10.0273ZM13.0339 5C13.0339 4.44772 12.5862 4 12.0339 4C11.4817 4 11.0339 4.44772 11.0339 5V5.5H10.5339C9.98165 5.5 9.53394 5.94772 9.53394 6.5C9.53394 7.05228 9.98165 7.5 10.5339 7.5H11.0339V8C11.0339 8.55228 11.4817 9 12.0339 9C12.5862 9 13.0339 8.55228 13.0339 8V7.5H13.5339C14.0862 7.5 14.5339 7.05228 14.5339 6.5C14.5339 5.94772 14.0862 5.5 13.5339 5.5H13.0339V5ZM10.0339 14.5085C10.5862 14.5085 11.0339 14.9563 11.0339 15.5085V16.5085C11.0339 17.0608 10.5862 17.5085 10.0339 17.5085C9.48165 17.5085 9.03394 17.0608 9.03394 16.5085V15.5085C9.03394 14.9563 9.48165 14.5085 10.0339 14.5085ZM14.0339 14.5085C14.5862 14.5085 15.0339 14.9563 15.0339 15.5085V16.5085C15.0339 17.0608 14.5862 17.5085 14.0339 17.5085C13.4817 17.5085 13.0339 17.0608 13.0339 16.5085V15.5085C13.0339 14.9563 13.4817 14.5085 14.0339 14.5085Z" fill="${fill}"/></svg>`,
        ) as string;
      case 'Internal Users':
        // Co Worker.svg
        return this.sanitizer.bypassSecurityTrustHtml(
          `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.5 6C8.5 4.89543 9.39543 4 10.5 4H13.5C14.6046 4 15.5 4.89543 15.5 6V6.13378C15.2011 6.30669 15 6.62986 15 7H9C9 6.62986 8.7989 6.30669 8.5 6.13378V6ZM6.5 6.13378V6C6.5 3.79086 8.29086 2 10.5 2H13.5C15.7091 2 17.5 3.79086 17.5 6V6.13378C17.7989 6.30669 18 6.62986 18 7H20.5C21.6046 7 22.5 7.89543 22.5 9V10.6421C22.356 10.642 22.2097 10.6731 22.0709 10.7391C19.0308 12.1833 15.6307 12.9942 12.039 13H11.9609C8.36931 12.9942 4.96922 12.1833 1.92908 10.7391C1.79033 10.6731 1.64402 10.642 1.5 10.6421V9C1.5 7.89543 2.39543 7 3.5 7H6C6 6.62986 6.2011 6.30669 6.5 6.13378ZM14.5 14.879C17.3264 14.6038 20.0189 13.867 22.5 12.7445V20C22.5 21.1046 21.6046 22 20.5 22H3.5C2.39543 22 1.5 21.1046 1.5 20V12.7445C3.98114 13.867 6.67359 14.6038 9.5 14.879V17C9.5 17.5523 9.94771 18 10.5 18H13.5C14.0523 18 14.5 17.5523 14.5 17V14.879ZM11.9921 15L12 15L12.0079 15C12.5566 15.0043 13 15.4504 13 16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16C11 15.4504 11.4434 15.0043 11.9921 15Z" fill="${fill}"/></svg>`,
        ) as string;
      case 'Programs':
        // Service.svg
        return this.sanitizer.bypassSecurityTrustHtml(
          `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.1299 3.05869C16.732 2.95412 17.7631 2.99363 18.3476 3.14267C18.8904 3.28112 19.1475 3.37952 19.6679 3.65048C21.3138 4.50752 22.5002 6.16578 22.8984 8.16611C23.024 8.79705 23.0354 9.95735 22.9218 10.5226C22.5458 12.3935 21.5162 14.1457 19.5654 16.2364C17.8831 18.0393 14.955 20.3573 12.58 21.7667C12.0455 22.0839 11.9436 22.082 11.3564 21.7354C10.3231 21.1256 8.53121 19.8673 7.32517 18.9054C5.6419 17.5629 4.00335 15.9342 3.01071 14.6153C2.01523 13.2927 1.37435 11.9794 1.09567 10.6915C1.01349 10.3116 0.998515 10.1146 0.999971 9.44541C1.00148 8.75497 1.01725 8.57869 1.11618 8.10849C1.25153 7.46521 1.42971 6.94307 1.69431 6.41513C2.63676 4.53484 4.24886 3.32851 6.22653 3.02548C6.51988 2.98054 7.53997 3.00367 7.87888 3.06259C8.67026 3.20035 9.43328 3.50867 10.1455 3.97959C10.6171 4.29157 11.4295 5.10058 11.7383 5.56552C11.8677 5.76053 11.9845 5.92002 11.997 5.92002C12.0117 5.91661 12.1235 5.76577 12.247 5.58115C12.6549 4.97187 13.3769 4.27627 14.0459 3.84873C14.5938 3.49872 15.435 3.1794 16.1299 3.05869ZM12 8.0665C11.3007 8.06664 10.7336 8.63382 10.7334 9.3331V11.8663H8.20017C7.50085 11.8663 6.93392 12.4337 6.93356 13.1329C6.93356 13.8324 7.50064 14.3995 8.20017 14.3995H10.7334V16.9327C10.7334 17.6322 11.3005 18.2001 12 18.2003C12.6994 18.2002 13.2666 17.6322 13.2666 16.9327V14.3995H15.7998C16.4993 14.3994 17.0674 13.8324 17.0674 13.1329C17.067 12.4337 16.499 11.8664 15.7998 11.8663H13.2666V9.3331C13.2663 8.63379 12.6993 8.0666 12 8.0665Z" fill="${fill}"/></svg>`,
        ) as string;
      case 'Audit Logs':
        // file-filled.svg
        return this.sanitizer.bypassSecurityTrustHtml(
          `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.6748 2.00012C15.1638 2.00012 15.4085 2.00054 15.6387 2.05579C15.8426 2.10479 16.0379 2.18544 16.2168 2.29504C16.4185 2.41872 16.5917 2.59186 16.9375 2.93762L19.0625 5.06262C19.4083 5.40838 19.5814 5.5816 19.7051 5.78333C19.8147 5.96219 19.8953 6.15748 19.9443 6.36145C19.9996 6.59158 20 6.83633 20 7.32532V17.2003C20 18.8803 19.9998 19.7207 19.6729 20.3624C19.3853 20.9266 18.9265 21.3854 18.3623 21.673C17.7206 21.9999 16.8802 22.0001 15.2002 22.0001H8.7998C7.11978 22.0001 6.27941 21.9999 5.6377 21.673C5.07347 21.3854 4.61472 20.9266 4.32715 20.3624C4.00018 19.7207 4 18.8803 4 17.2003V6.79993C4 5.1199 4.00018 4.27953 4.32715 3.63782C4.61472 3.07359 5.07347 2.61484 5.6377 2.32727C6.27941 2.0003 7.11978 2.00012 8.7998 2.00012H14.6748ZM15.3535 3.35364C15.0385 3.03873 14.5001 3.26177 14.5 3.70715V6.50012C14.5 7.05241 14.9477 7.50012 15.5 7.50012H18.293C18.7384 7.50006 18.9614 6.96158 18.6465 6.64661L15.3535 3.35364Z" fill="${fill}"/></svg>`,
        ) as string;
      case 'Settings':
        // cog/gear icon
        return this.sanitizer.bypassSecurityTrustHtml(
          `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z" fill="${fill}"/></svg>`,
        ) as string;
      default:
        return null;
    }
  }

  /**
   * Get the data-tutorial attribute value for a navigation item
   * Used by the tutorial system to highlight navigation elements
   */
  getTutorialDataAttr(label: string): string | null {
    switch (label) {
      case 'Hospitals':
        return 'hospitals-nav';
      case 'Hospital Users':
        return 'users-nav';
      case 'Internal Users':
        return 'internal-users-nav';
      case 'Programs':
        return 'programs-nav';
      case 'Analytics':
        return 'analytics-nav';
      default:
        return null;
    }
  }

  readonly navItems: NavItem[] = [
    {
      label: 'Referrals',
      icon: '', // SVG used
      route: '/',
      requiredPermission: 'canViewReferrals',
    },
    {
      label: 'Hospitals',
      icon: 'pi-building',
      route: '/hospitals',
      requiredGroups: [
        KEYCLOAK_GROUPS.SUPER_ADMIN,
        KEYCLOAK_GROUPS.INTERNAL_ADMIN,
      ],
    },
    {
      label: 'Hospital Users',
      icon: 'pi-users',
      route: '/hospital-users',
      requiredGroups: [
        KEYCLOAK_GROUPS.SUPER_ADMIN,
        KEYCLOAK_GROUPS.INTERNAL_ADMIN,
      ],
    },
    {
      label: 'Internal Users',
      icon: 'pi-user-plus',
      route: '/internal-users',
      requiredGroups: [
        KEYCLOAK_GROUPS.SUPER_ADMIN,
        KEYCLOAK_GROUPS.INTERNAL_ADMIN,
      ],
    },
    {
      label: 'Programs',
      icon: '', // SVG used
      route: '/programs',
      requiredPermission: 'canManagePrograms',
    },
    {
      label: 'Analytics',
      icon: 'pi-chart-bar',
      route: '/admin',
      requiredGroups: [
        KEYCLOAK_GROUPS.SUPER_ADMIN,
        KEYCLOAK_GROUPS.INTERNAL_ADMIN,
      ],
    },
    {
      label: 'Audit Logs',
      icon: 'pi-list-check',
      route: '/audit-logs',
      requiredGroups: [KEYCLOAK_GROUPS.SUPER_ADMIN],
    },
    {
      label: 'Program Definitions',
      icon: 'pi-book',
      route: '/program-definitions',
      requiredGroups: [KEYCLOAK_GROUPS.SUPER_ADMIN],
    },
    {
      label: 'Settings',
      icon: 'pi-cog',
      route: '/settings/notifications',
      // Available to all authenticated users
    },
  ];

  // Settings/preferences items - shown in separate section
  readonly settingsItems: NavItem[] = [];

  readonly visibleNavItems = computed(() => {
    const permissions = this.authService.userPermissions();
    const user = this.authService.currentUser();

    if (!permissions || !user) {
      return [];
    }

    return this.navItems.filter((item) => {
      // If no requirements specified, show the item
      if (
        !item.requiredPermission &&
        !item.requiredRoles &&
        !item.requiredGroups
      ) {
        return true;
      }

      let hasPermissionAccess = true;
      let hasRoleAccess = true;
      let hasGroupAccess = true;

      // Check permission requirement
      if (item.requiredPermission) {
        hasPermissionAccess = permissions[item.requiredPermission];
      }

      // Check role requirements
      if (item.requiredRoles && item.requiredRoles.length > 0) {
        if (item.requireAll) {
          hasRoleAccess = item.requiredRoles.every((role) =>
            user.roles.includes(role),
          );
        } else {
          hasRoleAccess = item.requiredRoles.some((role) =>
            user.roles.includes(role),
          );
        }
      }

      // Check group requirements
      if (item.requiredGroups && item.requiredGroups.length > 0) {
        if (item.requireAll) {
          hasGroupAccess = item.requiredGroups.every((group) =>
            user.groups.some((userGroup) => userGroup.includes(group)),
          );
        } else {
          hasGroupAccess = item.requiredGroups.some((group) =>
            user.groups.some((userGroup) => userGroup.includes(group)),
          );
        }
      }

      // If multiple types of requirements are specified, user must satisfy all types
      return hasPermissionAccess && hasRoleAccess && hasGroupAccess;
    });
  });

  // Main navigation items (modules) - filtered by permissions
  readonly mainNavItems = computed(() => this.visibleNavItems());

  // Settings navigation items - always visible to authenticated users
  readonly settingsNavItems = computed(() => {
    const user = this.authService.currentUser();
    if (!user) return [];
    return this.settingsItems;
  });

  isActive(route: string): boolean {
    const isActive =
      this.router.url === route ||
      (this.router.url.startsWith(route + '/') && route !== '/');
    return isActive;
  }

  logout(): void {
    this.authService.logout();
  }
}
